import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Sparkles, ChevronRight, Phone, Award, ArrowRight, Scissors, Palette } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { services, testimonials, salonInfo } from '../data/mock';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Enhanced Hero Section with Split Layout */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-[#EBF4F6] via-white to-[#EBF4F6]/50">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating Gradient Orbs */}
          <div className="absolute top-20 right-[10%] w-96 h-96 bg-gradient-to-br from-[#3D7A8C]/20 to-[#5DA7B7]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-[15%] w-80 h-80 bg-gradient-to-br from-[#86BDC9]/20 to-[#3D7A8C]/10 rounded-full blur-3xl"></div>
          
          {/* Geometric Shapes */}
          <div className="absolute top-40 left-[5%] w-24 h-24 border-2 border-[#3D7A8C]/20 rounded-2xl rotate-12"></div>
          <div className="absolute bottom-40 right-[8%] w-32 h-32 border-2 border-[#3D7A8C]/30 rounded-full"></div>
          <div className="absolute top-[60%] right-[20%] w-16 h-16 bg-gradient-to-br from-[#3D7A8C]/10 to-transparent rounded-lg rotate-45"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              {/* Premium Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-md border border-[#3D7A8C]/20">
                <Award className="w-4 h-4 text-[#3D7A8C]" />
                <span className="text-sm font-medium text-gray-700">Premier Beauty Destination</span>
              </div>

              {/* Main Heading */}
              <div className="space-y-4">
                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-gray-900">
                  {salonInfo.name}
                </h1>
                <p className="text-2xl sm:text-3xl font-serif font-light italic text-gray-600">
                  {salonInfo.tagline}
                </p>
              </div>

              {/* Description */}
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                Experience premium beauty and wellness services where artistry meets elegance. Our expert team is dedicated to making you look and feel your absolute best.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/services">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto group bg-[#3D7A8C] hover:bg-[#356A7A] text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl"
                  >
                    Explore Our Services
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                
                <a href={`tel:${salonInfo.phone}`}>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto border-2 border-[#3D7A8C] text-[#3D7A8C] hover:bg-[#3D7A8C]/10 px-8 py-6 text-base font-semibold rounded-xl transition-all duration-300"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us
                  </Button>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center">
                    <Star className="w-6 h-6 fill-[#3D7A8C] text-[#3D7A8C]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Award-Winning</p>
                    <p className="text-xs text-gray-500">Expert Team</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-[#3D7A8C]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Premium</p>
                    <p className="text-xs text-gray-500">Products</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-[#3D7A8C]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">12+ Years</p>
                    <p className="text-xs text-gray-500">Excellence</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative hidden lg:block">
              {/* Feature Cards Stack */}
              <div className="relative h-[600px]">
                {/* Main Card */}
                <div className="absolute top-0 right-0 w-[400px] h-[500px] rounded-3xl overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <img 
                    src="https://images.unsplash.com/photo-1610173826014-d131b02d69ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMG1ha2V1cCUyMGJyaWRhbHxlbnwwfHx8fDE3ODIxNDQ2NzJ8MA&ixlib=rb-4.1.0&q=85" 
                    alt="Beauty Services"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <Badge className="mb-2 bg-white/90 text-gray-900 border-0 backdrop-blur-sm">
                      Signature Service
                    </Badge>
                    <h3 className="text-2xl font-serif font-bold text-white">Transform Your Look</h3>
                  </div>
                </div>

                {/* Floating Service Cards */}
                <div className="absolute top-12 left-0 w-56 bg-white rounded-2xl shadow-xl p-5 transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center">
                      <Scissors className="w-6 h-6 text-[#3D7A8C]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Hair Styling</p>
                      <p className="text-xs text-gray-500">Expert Care</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`hair-star-${i}`} className="w-4 h-4 fill-[#3D7A8C] text-[#3D7A8C]" />
                    ))}
                  </div>
                </div>

                <div className="absolute bottom-24 left-12 w-56 bg-white rounded-2xl shadow-xl p-5 transform rotate-6 hover:rotate-0 transition-transform duration-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-[#3D7A8C]/10 flex items-center justify-center">
                      <Palette className="w-6 h-6 text-[#3D7A8C]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Makeup Artistry</p>
                      <p className="text-xs text-gray-500">Flawless Beauty</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`makeup-star-${i}`} className="w-4 h-4 fill-[#3D7A8C] text-[#3D7A8C]" />
                    ))}
                  </div>
                </div>

                <div className="absolute top-[45%] right-12 w-48 bg-[#3D7A8C] rounded-2xl shadow-xl p-5 text-white">
                  <p className="text-4xl font-bold mb-1">500+</p>
                  <p className="text-sm font-medium">Happy Clients</p>
                  <p className="text-xs text-white/70 mt-1">This Month</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#3D7A8C]/10 text-[#3D7A8C] border-0 px-4 py-2 text-sm font-medium">
              Our Services
            </Badge>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Elevate Your Beauty Experience
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From hair transformations to spa treatments, we offer a complete range of premium beauty services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link key={service.id} to={`/services`}>
                <Card className="group cursor-pointer overflow-hidden border-2 border-gray-100 hover:border-[#3D7A8C]/50 hover:shadow-2xl transition-all duration-300 rounded-2xl h-full">
                  <div className="relative h-72 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10"></div>
                    <img
                      src={service.image}
                      alt={service.category}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute bottom-6 left-6 right-6 z-20">
                      <h3 className="font-serif text-3xl font-bold text-white mb-2">{service.category}</h3>
                      <p className="text-gray-200 text-sm">{service.items.length} services available</p>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <Button className="w-full bg-[#3D7A8C] hover:bg-[#356A7A] text-white rounded-lg font-medium transition-all duration-300 group-hover:shadow-lg">
                      View Services
                      <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#3D7A8C]/10 text-[#3D7A8C] border-0 px-4 py-2 text-sm font-medium">
              Testimonials
            </Badge>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real experiences from our valued clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card key={`testimonial-${testimonial.name}-${index}`} className="border-2 border-gray-100 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`testimonial-${index}-star-${i}`} className="w-5 h-5 fill-[#3D7A8C] text-[#3D7A8C]" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">&ldquo;{testimonial.text}&rdquo;</p>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#3D7A8C] flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.service}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-[#3D7A8C] relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-6">
            Visit Us Today
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Experience the luxury you deserve at {salonInfo.name}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${salonInfo.phone}`}>
              <Button size="lg" className="bg-white text-[#3D7A8C] hover:bg-gray-50 px-10 py-7 text-lg font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300">
                <Phone className="w-6 h-6 mr-2" />
                Call Us Now
              </Button>
            </a>
            <Link to="/location">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-7 text-lg font-semibold rounded-xl transition-all duration-300">
                Get Directions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
